import React, { useState } from 'react';
import { Box, Button, Divider, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { Extension } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog, DateField, FormDialog, InlineMessage, LoadingState, SectionCard, StatusChip } from '../../components/ui';
import { useToast } from '../../contexts/ToastContext';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import {
  AddonBillingMode,
  AdminAddonRow,
  AdminAddonsResponse,
  MODE_LABEL,
  addonChangeSummary,
  addonCoverageLine,
  addonDayLabel,
  addonPriceLabel,
  addonRemoveMessage,
  addonSubmitLabel,
  initialMode,
  modeBlockedReason,
  modesOffered,
} from './storeAddons';

const readBody = async (response: Response, fallback: string): Promise<AdminAddonsResponse> => {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) {
    throw new Error(body?.error?.message || body?.message || fallback);
  }
  return body.data as AdminAddonsResponse;
};

const todayLocal = (): string => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const ModeChip: React.FC<{ row: AdminAddonRow }> = ({ row }) => {
  if (row.billingMode === 'billed') return <StatusChip label="Billed" tone="info" />;
  if (row.billingMode === 'free') return <StatusChip label="Free" tone="positive" />;
  if (row.billingMode === 'trial') {
    return <StatusChip label={`Trial through ${addonDayLabel(row.trialEndsOn || '')}`} tone="caution" />;
  }
  if (row.includedInPlan) return <StatusChip label="Included in plan" tone="positive" />;
  if (row.trialEndedOn) return <StatusChip label={`Trial ended ${addonDayLabel(row.trialEndedOn)}`} tone="neutral" />;
  return null;
};

/**
 * Alex adds and removes a store's a la carte add-ons (MOT-837). Billed is the
 * default and is a real charge, so the dialog names the amount and interval on
 * its button. Renders nothing while `admin_plan_addons` is off for the store.
 */
export const StoreAddonsCard: React.FC<{ orgId: string; storeName: string }> = ({ orgId, storeName }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const queryKey = ['admin-store-addons', orgId];
  const [editing, setEditing] = useState<AdminAddonRow | null>(null);
  const [mode, setMode] = useState<AddonBillingMode>('billed');
  const [trialEndsOn, setTrialEndsOn] = useState('');
  const [reason, setReason] = useState('');
  const [removing, setRemoving] = useState<AdminAddonRow | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: async () =>
      readBody(
        await makeApiCall(`/billing/admin/organizations/${orgId}/addons`, { headers: await getAuthHeaders() }),
        'Could not load add-ons',
      ),
  });

  const settled = (next: AdminAddonsResponse) => {
    queryClient.setQueryData(queryKey, next);
    // The store's limits moved, so the entitlements shown beside this card did too.
    queryClient.invalidateQueries({ queryKey: ['admin-access-grant', orgId] });
  };

  const save = useMutation({
    mutationFn: async (row: AdminAddonRow) =>
      readBody(
        await makeApiCall(`/billing/admin/organizations/${orgId}/addons/${row.key}`, {
          method: 'PUT',
          headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mode,
            ...(mode === 'trial' ? { trialEndsOn } : {}),
            ...(reason.trim() ? { reason: reason.trim() } : {}),
          }),
        }),
        'Could not save this add-on',
      ),
    onSuccess: (next, row) => {
      settled(next);
      showToast(`${row.name}: ${MODE_LABEL[mode]}`, 'success');
      setEditing(null);
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const remove = useMutation({
    mutationFn: async (row: AdminAddonRow) =>
      readBody(
        await makeApiCall(`/billing/admin/organizations/${orgId}/addons/${row.key}`, {
          method: 'DELETE',
          headers: await getAuthHeaders(),
        }),
        'Could not remove this add-on',
      ),
    onSuccess: (next, row) => {
      settled(next);
      showToast(`${row.name} removed`, 'success');
      setRemoving(null);
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  if (!isLoading && !isError && !data?.enabled) return null;

  const openEditor = (row: AdminAddonRow) => {
    setMode(initialMode(row));
    setTrialEndsOn(row.billingMode === 'trial' ? row.trialEndsOn || '' : '');
    setReason('');
    setEditing(row);
  };

  const rows = data?.addons ?? [];
  const live = rows.filter((row) => !row.comingSoon || row.billingMode);
  const pickedBlocked = editing ? modeBlockedReason(editing, mode) : null;
  const needsDate = mode === 'trial' && !trialEndsOn;
  const summary = editing
    ? addonChangeSummary(editing, mode, { storeName, trialEndsOn, stripeTrialing: data?.stripeTrialing })
    : [];

  return (
    <SectionCard
      title="Add-ons"
      subtitle={rows.length > 0 ? addonCoverageLine(rows) : undefined}
      leading={<Extension color="primary" />}
    >
      {isLoading && <LoadingState />}
      {isError && <InlineMessage severity="error">{(error as Error).message}</InlineMessage>}
      {data?.enabled && !data.hasStripeSubscription && (
        <InlineMessage severity="info">
          This store has no Stripe subscription, so add-ons can only be Free or Trial here.
        </InlineMessage>
      )}
      <Stack divider={<Divider flexItem />} spacing={0}>
        {live.map((row) => {
          const onStore = row.billingMode !== null;
          return (
            <Box key={row.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', py: 1.5 }}>
              <Stack spacing={0.25} sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {row.name}
                  </Typography>
                  <ModeChip row={row} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {row.description}
                </Typography>
                {onStore && row.addedBy && (
                  <Typography variant="caption" color="text.secondary">
                    Added by {row.addedBy}
                    {row.addedAt ? ` on ${new Date(row.addedAt).toLocaleDateString()}` : ''}
                  </Typography>
                )}
                {row.inactiveOnFreeTier && (
                  <Typography variant="caption" color="warning.main">
                    Grants nothing while this store is on Free Preview. It comes back on a paid plan.
                  </Typography>
                )}
                {!onStore && row.addBlockedReason && !row.includedInPlan && (
                  <Typography variant="caption" color="text.secondary">
                    {row.addBlockedReason}
                  </Typography>
                )}
              </Stack>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 90 }}>
                {addonPriceLabel(row)}
              </Typography>
              <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                {onStore ? (
                  <>
                    <Button size="small" variant="outlined" onClick={() => openEditor(row)}>
                      Change billing
                    </Button>
                    <Button size="small" color="error" onClick={() => setRemoving(row)}>
                      Remove
                    </Button>
                  </>
                ) : (
                  <>
                    {!row.addBlockedReason && (
                      <Button size="small" variant="outlined" onClick={() => openEditor(row)}>
                        Add
                      </Button>
                    )}
                    {row.trialEndedOn && (
                      <Button size="small" onClick={() => setRemoving(row)}>
                        Clear
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>

      <FormDialog
        open={Boolean(editing)}
        title={editing ? `${editing.billingMode ? 'Change billing for' : 'Add'} ${editing.name}` : ''}
        onClose={() => setEditing(null)}
        onSubmit={() => editing && save.mutate(editing)}
        submitLabel={editing ? addonSubmitLabel(editing, mode) : ''}
        submitDisabled={Boolean(pickedBlocked) || needsDate}
        loading={save.isPending}
      >
        {editing && (
          <Stack spacing={1.5}>
            <RadioGroup value={mode} onChange={(event) => setMode(event.target.value as AddonBillingMode)}>
              {modesOffered(editing).map((option) => {
                const blocked = modeBlockedReason(editing, option);
                return (
                  <Box key={option}>
                    <FormControlLabel
                      value={option}
                      disabled={Boolean(blocked)}
                      control={<Radio size="small" />}
                      label={option === 'billed' ? `Billed, ${addonPriceLabel(editing)}` : MODE_LABEL[option]}
                    />
                    {blocked && (
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 3.5 }}>
                        {blocked}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </RadioGroup>
            {mode === 'trial' && (
              <DateField
                label="Last day of the trial"
                value={trialEndsOn}
                onChange={setTrialEndsOn}
                min={todayLocal()}
                size="small"
                helperText="In the store's time zone."
              />
            )}
            <TextField
              label="Reason (optional, kept in the audit log)"
              size="small"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              inputProps={{ maxLength: 500 }}
            />
            <Stack spacing={0.5}>
              {summary.map((line) => (
                <Typography key={line} variant="body2">
                  {line}
                </Typography>
              ))}
            </Stack>
          </Stack>
        )}
      </FormDialog>

      <ConfirmDialog
        open={Boolean(removing)}
        title={removing?.billingMode ? `Remove ${removing?.name}?` : 'Clear this ended trial?'}
        message={removing ? addonRemoveMessage(removing, storeName) : ''}
        confirmLabel={removing?.billingMode ? 'Remove' : 'Clear'}
        confirmColor="error"
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing)}
        onClose={() => setRemoving(null)}
      />
    </SectionCard>
  );
};
